import React, { useContext, useEffect, useState } from "react";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { isAxiosError } from "axios";
import { useMusicStore } from "../store/store";
import { PlayModes } from "../../types/PlayModes";
import {
    getCurrentQueueItem,
    getRandomRecordings,
    getRandomRecordingsByArtist,
    getRandomRecordingsByGenre,
    getRandomRecordingsBySimilarArtist,
    getRandomRecordingsEmphasizingArtists,
    getSuggestion
} from "../apis/music-api";
import { Recording } from "../../types/Recording";
import { AuthenticationContext } from "../authentication/AuthenticationContext";
import { LastFmPlaylistItem } from "../../types/LastFmPlaylist";
import { format, zeroPad } from "../text-helper/text-helper";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { RandomOptions } from "../../types/RandomOptions";
import PlayModeDisplay from "../playmode-display/PlayModeDisplay";
import SearchField from "../search-field/SearchField";
import JukeboxSelector from "./JukeboxSelector";
import { Link } from "react-router-dom";

const ControlBar = () => {
    const { getMusicToken, getAccessToken } = useContext(AuthenticationContext);
    const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const isLessThan1000 = useMediaQuery('(max-width:1000px)');
    const [queueRecording, setQueueRecording] = useState<Recording | undefined>(undefined);
    const [storedRandomOption, setStoredRandomOption] = useLocalStorage('random-option', RandomOptions.Unknown);
    const {
        isPlaying,
        maxDuration,
        genreId,
        artistName,
        playMode,
        setPlaylist,
    } = useMusicStore();
     
    const apiUrl = process.env.REACT_APP_API_URL;
    const timer = React.useRef<NodeJS.Timeout | null>(null);
    const isQueuePollingActive = React.useRef(false);

    const scheduleQueuePoll = (delayInMs: number) => {
        if (timer.current !== null) {
            clearTimeout(timer.current);
        }

        timer.current = setTimeout(fetchQueueItem, delayInMs);
    };

    const play = (recording: Recording) => {
        if (recording !== undefined) {
            const musicToken = getMusicToken();
            const albumName = `${recording.albumYear} - ${recording.albumTitle}`;
            const trackName = `${zeroPad(recording?.track?.number ?? 0, 2)} - ${recording?.track?.name}`;
            const trackList = [
                {
                    letter: recording?.letter,
                    artist: recording?.artistName,
                    album: albumName,
                    trackNumber: recording?.track?.number,
                    name: recording?.track?.name,
                    scrobbleArtist: format(recording?.artistName ?? ''),
                    src: `${apiUrl}/Stream/Artists/${recording.artistName}/Albums/${albumName}/Tracks/${trackName}?accessToken=${musicToken}`,
                    progressSeconds: recording?.progressSeconds ?? 0,
                } as LastFmPlaylistItem
            ];

            setPlaylist(trackList);
        }
    }
    
    const fetchQueueItem = async () => {
        if (!isQueuePollingActive.current) {
            return;
        }

        try {
            const queueResponse = await getCurrentQueueItem(getAccessToken());
            if (queueResponse?.recording?.track === undefined) {
                scheduleQueuePoll(5000);
                return;
            }

            setQueueRecording(queueResponse?.recording);

            // Avoid immediate re-fire loops when backend returns an invalid or expired remaining time.
            const nextPollInMs = Math.max(queueResponse.timeRemaininginMilliSeconds ?? 0, 1000);
            scheduleQueuePoll(nextPollInMs);
        } catch (error: unknown) {
            if (isAxiosError(error) && error.response?.status === 404) {
                // Queue endpoint returns 404 when no current item exists. Retry instead of surfacing noise.
                setQueueRecording(undefined);
                scheduleQueuePoll(5000);
                return;
            }

            console.error('Failed to fetch queue item', error);
            scheduleQueuePoll(5000);
        }
    };

    useEffect(() => {
        isQueuePollingActive.current = true;
        fetchQueueItem();

        return () => {
            isQueuePollingActive.current = false;
            if (timer.current !== null) {
                clearTimeout(timer.current);
                timer.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!isPlaying && playMode !== PlayModes.Unknown) {
            const playRecording = async () => {
                try
                {
                    const recordings = await getRecording(playMode);
                    if (recordings !== undefined && recordings.length > 0) {
                        const recording = recordings[0];
                        play(recording);
                    }
                }
                catch
                {
                }
            }

            playRecording();
        }
    }, [isPlaying, playMode, genreId, artistName]);

    const getRecording = async (playMode: PlayModes): Promise<Recording[]> => {
        switch (playMode) {
            case PlayModes.Genre:
                return await getRandomRecordingsByGenre(getAccessToken(), 1, maxDuration, genreId ?? 0);
            case PlayModes.Artist:
                return await getRandomRecordingsByArtist(getAccessToken(), 1, maxDuration, artistName ?? '');
            case PlayModes.Similar:
                return await getRandomRecordingsBySimilarArtist(getAccessToken(), 1, maxDuration, artistName ?? '');
            case PlayModes.Queue: {
                const currentQueueResponse = await getCurrentQueueItem(getAccessToken());
                const progressSeconds = ((currentQueueResponse.recording?.track?.duration ?? 0) - (currentQueueResponse.timeRemaininginMilliSeconds ?? 0)) / 1000;
                return currentQueueResponse !== undefined ? [{
                    ...currentQueueResponse.recording,
                    progressSeconds: progressSeconds
                }] : [];
            }
            case PlayModes.NoRules: {
                if (storedRandomOption === RandomOptions.EmphasizeArtists) {
                    return await getRandomRecordingsEmphasizingArtists(getAccessToken(), 1, maxDuration);
                }

                return await getRandomRecordings(getAccessToken(), 1, maxDuration);
            }
            case PlayModes.AiGenerated:
                return [await getSuggestion(getAccessToken())];
            default:
                return []
        }
    }

    const getModeName = (playMode: PlayModes): string => {
        switch (playMode) {
            case PlayModes.Genre: return "Genre";
            case PlayModes.Artist: return "Artist";
            case PlayModes.Similar: return "Similar Artist";
            case PlayModes.NoRules: return "No Rules";
            case PlayModes.AiGenerated: return "Smart Shuffle";
            case PlayModes.Album: return "Album";
            case PlayModes.Compilation: return "Compilation";
            case PlayModes.Track: return "Track";
            case PlayModes.CompilationTrack: return "Compilation Track";
            case PlayModes.AlbumPlaylist: return "Album Playlist";
            case PlayModes.TrackPlaylist: return "Track Playlist";
            case PlayModes.Queue: return "Queue";
            default: return "Stopped";
        }
    }

    return (
        <Stack spacing={1}>
            <Box>
                <Stack
                    display="flex" 
                    flexDirection={isLessThan1000 ? "column" : "row"}
                    flexWrap="wrap"
                    sx={{ width: '100%' }}
                >
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-start"
                        sx={{
                            flexBasis: isLessThan1000 ? '100%' : '50%',
                            maxWidth: isLessThan1000 ? '100%' : '50%'
                        }}
                    >
                        <JukeboxSelector />
                    </Box>
                    <Box
                        display="flex"
                        flexDirection={isLessThan1000 ? "column" : "row"}
                        justifyContent="flex-end"
                        sx={{
                            flexBasis: isLessThan1000 ? '100%' : '50%',
                            maxWidth: isLessThan1000 ? '100%' : '50%'
                        }}
                    >
                        <SearchField />
                    </Box>
                </Stack>
            </Box>
            <Box>
                <Stack
                    display="flex"
                    flexDirection={isLessThan1000 ? "column" : "row"}
                >
                    <Box display="flex" justifyContent="flex-start" sx={{ flexGrow: 1, minWidth: 0 }}>
                        {isPlaying &&
                            <Stack
                                direction="row"
                                gap={2}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                <Box>
                                    <Typography variant="subtitle2">
                                        Current Mode: <b>{getModeName(playMode)}</b>
                                    </Typography>
                                </Box>
                                <Box>
                                    <PlayModeDisplay mode={playMode} />
                                </Box>
                                {!isSmall && <Box sx={{ width: '72px' }} />}
                            </Stack>
                        }
                    </Box>
                    <Box display="flex" justifyContent="flex-end" sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2">
                            In The Queue:&nbsp;
                            <Link
                                style={{ textDecoration: 'none', color: 'inherit' }}
                                to={`/letters/${queueRecording?.letter}/artists/${queueRecording?.artistName}/albums/${queueRecording?.albumYear} - ${queueRecording?.albumTitle}`}
                            >
                                <b>{queueRecording?.track?.name}</b>
                            </Link>
                            &nbsp;by&nbsp;
                            <Link
                                style={{ textDecoration: 'none', color: 'inherit' }}
                                to={`/letters/${queueRecording?.letter}/artists/${queueRecording?.artistName}`}
                            >
                                <b>{format(queueRecording?.artistName ?? '')}</b>
                            </Link>
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        </Stack>
    );
}

export default ControlBar;